/**
 * FloatingHorizontalScrollbar - keeps a table's horizontal scrollbar reachable.
 *
 * Wide tables (e.g. the Projects list with many columns made visible) scroll
 * horizontally inside their own container, but that container's native
 * scrollbar sits underneath the last row. When the table is taller than the
 * viewport the scrollbar is off-screen, so the columns to the right appear
 * unreachable.
 *
 * This component renders a slim scrollbar pinned to the bottom of the viewport
 * whenever the target container overflows horizontally and its own scrollbar is
 * below the fold. Scroll position is kept in sync in both directions. It is
 * portalled to `document.body` so ancestor `overflow: hidden` cannot clip it,
 * and it is only mounted when needed so it never affects layout.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const BAR_HEIGHT = 12;

interface FloatingScrollbarGeometry {
  left: number;
  width: number;
  scrollWidth: number;
}

interface FloatingHorizontalScrollbarProps {
  /** The horizontally scrolling container to mirror */
  targetRef: React.RefObject<HTMLElement | null>;
}

export function FloatingHorizontalScrollbar({
  targetRef,
}: FloatingHorizontalScrollbarProps) {
  const barRef = useRef<HTMLDivElement | null>(null);
  const [geometry, setGeometry] = useState<FloatingScrollbarGeometry | null>(
    null,
  );

  useEffect(() => {
    const target = targetRef.current;
    if (!target || typeof window === 'undefined') return;

    const update = () => {
      const overflows = target.scrollWidth - target.clientWidth > 1;
      const rect = target.getBoundingClientRect();
      const viewportBottom = window.innerHeight;

      // Only show the floating bar while the container crosses the bottom of the
      // viewport — i.e. its own scrollbar is out of reach. This also guarantees
      // at most one bar when several grids are stacked (grouped views).
      const scrollbarBelowFold =
        rect.top < viewportBottom - BAR_HEIGHT && rect.bottom > viewportBottom;

      if (!overflows || !scrollbarBelowFold) {
        setGeometry(null);
        return;
      }

      setGeometry((prev) => {
        const next = {
          left: rect.left,
          width: target.clientWidth,
          scrollWidth: target.scrollWidth,
        };
        if (
          prev &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.scrollWidth === next.scrollWidth
        ) {
          return prev;
        }
        return next;
      });
    };

    const syncFromTarget = () => {
      const bar = barRef.current;
      if (bar && bar.scrollLeft !== target.scrollLeft) {
        bar.scrollLeft = target.scrollLeft;
      }
    };

    update();

    target.addEventListener('scroll', syncFromTarget);

    // Capture phase so scrolling of any ancestor (scroll events do not bubble)
    // and of the container itself both trigger a recalculation.
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(target);
    const content = target.firstElementChild;
    if (content) resizeObserver.observe(content);

    return () => {
      target.removeEventListener('scroll', syncFromTarget);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      resizeObserver.disconnect();
    };
  }, [targetRef]);

  const handleBarScroll = useCallback(() => {
    const target = targetRef.current;
    const bar = barRef.current;
    if (!target || !bar) return;
    if (target.scrollLeft !== bar.scrollLeft) {
      target.scrollLeft = bar.scrollLeft;
    }
  }, [targetRef]);

  // Mirror the container's current offset as soon as the bar mounts
  useEffect(() => {
    const target = targetRef.current;
    const bar = barRef.current;
    if (geometry && target && bar) {
      bar.scrollLeft = target.scrollLeft;
    }
  }, [geometry, targetRef]);

  if (!geometry) return null;

  return createPortal(
    <div
      ref={barRef}
      data-testid="floating-horizontal-scrollbar"
      onScroll={handleBarScroll}
      className="fixed z-30 overflow-x-auto overflow-y-hidden scrollbar-visible bg-card border-t"
      style={{
        left: geometry.left,
        width: geometry.width,
        bottom: 0,
        height: BAR_HEIGHT,
      }}
    >
      <div style={{ width: geometry.scrollWidth, height: 1 }} />
    </div>,
    document.body,
  );
}
